<template>
  <article v-if="news" class="flex flex-col">
    <div class="mb-[120px] flex gap-[20px]">
      <div class="flex flex-col xl:w-3/4 2xl:w-4/5">
        <BaseTheContentBreadcrumbs :current-page-title="news.title" />
        <h1
          v-if="isOldNewsFormat"
          class="mb-[28px] text-[32px] font-medium leading-[38px]"
          v-text="news.title"
        />
        <CommonBlocksImageArticleBlock
          v-if="isOldNewsFormat && news.image"
          class="mb-[32px]"
          :published-at="news.published_at"
          :src="news.image.src"
        />
        <EditorParser v-if="news?.json_body" :json-str="news?.json_body">
          <EditorMainImage
            :image-src="getSrc"
            :title="news.title"
            :announce="news.announce"
            :source="news.cover_source"
            :description="news.cover_description"
            :time="news.time"
          />
        </EditorParser>
        <div
          v-else
          ref="pageBodyRef"
          class="page-body mb-[65px]"
          v-html="news.body"
        />
        <CommonMobileBannerBlock v-if="false" />
        <CommonBlocksNewTagsBlock
          v-if="tags"
          class="mb-[80px]"
          v-bind="{ tags }"
        />

        <CommonSocialsSocialRedesign
          v-if="news.id"
          :id="news.id"
          :model-type="news.model_type ?? 'news'"
          :views="news.views"
          class="mb-[80px]"
        />

        <div v-if="news.authors?.length" class="flex flex-col">
          <h3 class="mb-[36px] text-[24px] font-medium">Автор</h3>
          <div class="grid grid-flow-row gap-5">
            <CommonAuthorInfo
              v-for="author in news.authors"
              :key="author.id"
              :author="author"
            />
          </div>
        </div>
      </div>
      <CommonBlocksRightArticleBlock class="xl:w-1/4 2xl:w-1/5" />
    </div>

    <CommonBlocksPageFooterBlocks />
  </article>
</template>

<script setup>
definePageMeta({
  layout: 'marginless',
});

const route = useRoute();
const slug = route?.params?.slug;
const { newsSlug } = useNewsBySlug(slug);
useErrorHandler(() => newsSlug.failureReason.value?.status);

const { isDesktop, isPad, isMobile } = useBreakpoint();
const news = computed(() => newsSlug?.data?.value);
const tags = computed(() => news.value?.tags);

const robots = ref('all');
const pageBodyRef = ref(null);

const isOldNewsFormat = computed(() => news.value?.json_body === null);

const seo = computed(() => news.value?.seo);
if (seo.value) {
  const arr = [];
  if (seo.value.index === false) arr.push('noindex');
  if (seo.value.follow === false) arr.push('nofollow');
  if (seo.value.archive === false) arr.push('noarchive');
  if (arr.length > 0) {
    robots.value = arr.reduce((previousValue, currentValue) => {
      return `${previousValue}, ${currentValue}`;
    });
  }
}

const getSrc = computed(() => {
  const src =
    news.value?.cover_image ||
    (isDesktop.value && news.value?.image?.desktop) ||
    (isPad.value && news.value?.image?.desktop) ||
    (isMobile.value && news.value?.image?.mobile) ||
    news.value?.image?.src;
  return src;
});

onMounted(() => {
  renderImageFooter(pageBodyRef.value);
});

useHead(
  computed(() => ({
    title: `${
      news.value?.title ? news.value.title + ' |' : ''
    } Авторы Федерального портала Истории России`,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: `События истории России. ${
          seo.value?.metadescription ?? news.value?.title ?? ''
        }. События истории России на Федеральном портале истории России.`,
      },
      {
        'http-equiv': 'Last-Modified',
        content: new Date(seo.value?.last_modified).toUTCString(),
      },
      {
        hid: 'robots',
        name: 'robots',
        content: robots.value,
      },
      {
        hid: 'og:image',
        property: 'og:image',
        content: `${useHostApp()}${news.value?.image?.src}` || '',
      },
      {
        hid: 'og:type',
        property: 'og:type',
        content: 'article',
      },
      {
        property: 'og:title',
        content: `${
          news.value?.title ? news.value.title + ' |' : ''
        } Авторы Федерального портала Истории России`,
      },
      {
        property: 'og:url',
        content: `${useHostApp()}/read/news/${slug}`,
      },
      {
        property: 'og:description',
        content: `События истории России. ${
          seo.value?.metadescription ?? news.value?.title ?? ''
        }. События истории России на Федеральном портале истории России.`,
      },
    ],
    link: [
      {
        rel: 'canonical',
        href: `${useHostApp()}${route.path}`,
      },
    ],
  })),
);

useSchemaOrg(() =>
  defineArticle({
    datePublished: new Date(news.value?.published_at),
    image: {
      url: `${useHostApp()}${getSrc.value}`,
      width: 800,
      height: 600,
      caption: news.value?.image?.alt,
    },
    // keywords: news.value?.tags?.map((tag) => tag.title),
  }),
);
</script>
